import React from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem,
  Button, Alert, CircularProgress, Divider, Chip, Stack
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EuroIcon from '@mui/icons-material/Euro';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { italianProvinces } from './data/zoneMap';

const DURATIONS = [
  { value: "12", label: "12 mesi" },
  { value: "24", label: "24 mesi" },
  { value: "36", label: "36 mesi" },
  { value: "48", label: "48 mesi" },
  { value: "60", label: "60 mesi" }
];

export default function QuoteForm({ formData, setFormData, onCalculate, isLoading, errors }) {
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Card elevation={3}>
      <CardContent sx={{ p: 0 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 2,
            bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <DirectionsCarIcon sx={{ color: 'primary.contrastText' }} />
          </Box>
          <Typography variant="h6" fontWeight={600}>Dati Preventivo</Typography>
        </Box>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Targa */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <SearchIcon fontSize="inherit" /> Targa veicolo&nbsp;<em>(opzionale)</em>
            </Typography>
            <TextField
              fullWidth
              placeholder="ES. AB123CD"
              value={formData.plate || ''}
              onChange={(e) => handleChange('plate', e.target.value.toUpperCase())}
              inputProps={{ style: { fontFamily: 'monospace', letterSpacing: '0.15em', textTransform: 'uppercase' } }}
              size="small"
            />
          </Box>

          {/* Marca e Modello */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Marca"
              placeholder="Es. Volkswagen"
              value={formData.brand || ''}
              onChange={(e) => handleChange('brand', e.target.value)}
              error={!!errors?.brand}
              helperText={errors?.brand}
              size="small"
              fullWidth
            />
            <TextField
              label="Modello"
              placeholder="Es. Golf 8"
              value={formData.model || ''}
              onChange={(e) => handleChange('model', e.target.value)}
              error={!!errors?.model}
              helperText={errors?.model}
              size="small"
              fullWidth
            />
          </Box>

          {/* Provincia */}
          <TextField
            select
            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><LocationOnIcon fontSize="small" />Provincia di residenza *</Box>}
            value={formData.provinceCode || ''}
            onChange={(e) => {
              const province = italianProvinces.find(p => p.code === e.target.value);
              handleChange('provinceCode', e.target.value);
              handleChange('province', province?.name || '');
            }}
            error={!!errors?.province}
            helperText={errors?.province}
            size="small"
            fullWidth
          >
            {italianProvinces.map((p) => (
              <MenuItem key={p.code} value={p.code}>
                <strong>{p.code}</strong>&nbsp;— {p.name}
              </MenuItem>
            ))}
          </TextField>

          {/* Valore Veicolo */}
          <Box>
            <TextField
              label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><EuroIcon fontSize="small" />Valore veicolo *</Box>}
              type="number"
              placeholder="25000"
              value={formData.vehicleValue || ''}
              onChange={(e) => handleChange('vehicleValue', e.target.value)}
              error={!!errors?.vehicleValue}
              helperText={errors?.vehicleValue || 'Inserisci il valore di acquisto o attuale del veicolo'}
              InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>€</Typography> }}
              size="small"
              fullWidth
            />
          </Box>

          {/* Durata */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <CalendarTodayIcon fontSize="inherit" /> Durata copertura *
            </Typography>
            <Stack direction="row" spacing={1}>
              {DURATIONS.map((d) => (
                <Chip
                  key={d.value}
                  label={d.label}
                  onClick={() => handleChange('duration', d.value)}
                  color={formData.duration === d.value ? 'primary' : 'default'}
                  variant={formData.duration === d.value ? 'filled' : 'outlined'}
                  sx={{ flex: 1, cursor: 'pointer', fontWeight: formData.duration === d.value ? 700 : 400 }}
                />
              ))}
            </Stack>
          </Box>

          <Divider sx={{ borderStyle: 'dashed' }} />

          {/* Intestatario */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <PersonIcon fontSize="inherit" /> Intestatario&nbsp;<em>(facoltativo)</em>
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                placeholder="Nome"
                value={formData.firstName || ''}
                onChange={(e) => handleChange('firstName', e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                placeholder="Cognome"
                value={formData.lastName || ''}
                onChange={(e) => handleChange('lastName', e.target.value)}
                size="small"
                fullWidth
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Lascia vuoto per un preventivo anonimo
            </Typography>
          </Box>

          {/* Errors */}
          {errors && Object.keys(errors).length > 0 && (
            <Alert severity="error" icon={<ErrorOutlineIcon />}>
              {Object.values(errors).filter(Boolean).join('. ')}
            </Alert>
          )}

          {/* Submit */}
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={onCalculate}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
            sx={{ py: 1.5, fontWeight: 700, fontSize: '1rem' }}
          >
            {isLoading ? 'Calcolo in corso...' : 'Calcola Preventivi'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}